import { Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { toast } from "sonner";

import PageWrapper from "@/components/PageWrapper.tsx";
import Image from "@/components/base/Image.tsx";
import IconShoppingCart from "@/assets/icons/shopping-cart.svg?react";
import { logError } from "@/components/error-hanlder/error-handler.lib.ts";

import { useGetPartnerDetail, Product } from "./api";
import ProductCard from "./components/ProductCard.tsx";
import ProductDetailSheet from "./components/ProductDetailSheet.tsx";
import LoyaltyQRButton from "./components/LoyaltyQRButton.tsx";
import PartnerSingleLoading from "./PartnerSingleLoading.tsx";

const ErrorHandler = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    toast.error(t("data_fetching_error"));
    navigate("/partners");
  }, [t, navigate]);

  return null;
};

function BasePartnerSingle() {
  const { t } = useTranslation();
  const { partnerId } = useParams();
  const { data: partner } = useSuspenseQuery(useGetPartnerDetail(partnerId));
  const [selected, setSelected] = useState<Product | null>(null);

  return (
    <PageWrapper backPath="/partners">
      <div className="min-h-screen bg-dark pb-24">
        {/* Hero banner — full width, partner icon as background */}
        <div className="relative w-full aspect-[16/10] bg-dark overflow-hidden">
          {partner.icon ? (
            <div className="absolute inset-0">
              <Image
                urls={partner.icon}
                size="medium"
                imgClassName="w-full h-full object-cover"
                wrapperClassName="w-full h-full"
                alt={partner.name}
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex-center bg-gradient-to-br from-orange-100/20 via-dark to-dark">
              <IconShoppingCart className="size-20 text-orange-100/60" />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-dark to-transparent pointer-events-none" />
        </div>

        {/* Content */}
        <div className="container pt-4">
          <h1 className="text-xl font-bold text-white leading-130">
            {partner.name}
          </h1>
          {partner.description && (
            <p className="text-sm text-gray-1 leading-150 mt-2">
              {partner.description}
            </p>
          )}

          {/* Products section */}
          <h2 className="text-base font-bold text-white leading-130 mt-6 mb-3">
            {t("products")}
          </h2>

          {partner.products.length === 0 ? (
            <div className="rounded-2xl border border-white/[0.04] bg-gray-4 py-10 px-4 flex flex-col items-center gap-2">
              <IconShoppingCart className="size-10 text-gray-1/60" />
              <p className="text-sm text-gray-1 leading-130 text-center">
                {t("no_products")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {partner.products.map((p) => (
                <ProductCard key={p.id} product={p} onClick={setSelected} />
              ))}
            </div>
          )}
        </div>
      </div>

      <ProductDetailSheet
        product={selected}
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
      />

      <LoyaltyQRButton />
    </PageWrapper>
  );
}

export default function PartnerSinglePage() {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense fallback={<PartnerSingleLoading />}>
        <BasePartnerSingle />
      </Suspense>
    </ErrorBoundary>
  );
}
